import { ColumnTypes, TFilterWithViewAllRef } from '@/components';
import {
	ALL_DOCUMENT_OPTIONS,
	FILE_ACCEPT,
	FILE_SIZE_10MB,
	FILE_SIZE_10MB_BYTE,
	FileTypeEnum,
	FormikValues,
	IMAGE_TYPE,
	MAX_FILE_UPLOAD,
	PageSize,
	PageTypeEnum,
	TableTypes,
} from '@/constant';
import { useGetListData, useRowSelection } from '@/hooks';
import { TDownloadDefineDocumentParams } from '@/services';
import {
	CommonActions,
	DefineDocumentAreaActions,
	DefineDocumentAreaSelectors,
	TDefineDocumentAreaDetailStore,
	useAppSelector,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import {
	getFileNameFromPath,
	isFileTypeInvalid,
	NOOP,
	showAlert,
	showToast,
} from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { DefaultOptionType } from 'antd/es/select';

import { isNull } from 'ramda-adjunct';

import { getColumns, getDocuments } from './define-document-area.config';
import { IDefineDocumentAreaProps } from './define-document-area.type';
export const useLogic = (props: IDefineDocumentAreaProps) => {
	const { id } = useParams<{ id: string }>();

	const { total: totalCount } = useGetListData({
		type: TableTypes.DEFINE_DOCUMENT_AREA,
	});
	const selectParams = useAppSelector(DefineDocumentAreaSelectors.selectParams);
	const { t } = useTranslation(['documentArea', 'validation', 'message']);
	const list = useAppSelector(
		DefineDocumentAreaSelectors.selectDefineDocumentAreaList
	);

	const { unitId } = useAppSelector(MyPageSelectors.selectDataMyPage);

	const dispatch = useDispatch();
	const rowSelection = useRowSelection<{}, TDefineDocumentAreaDetailStore>();
	const filterRef = useRef<TFilterWithViewAllRef>(null);
	const [page, setPage] = useState({
		current: 1,
		pageSize: PageSize.S,
	});
	const [loading, setLoading] = useState(false);
	const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
	const [fileList, setFileList] = useState<File[]>([]);
	const { params: updateHistoryParams } = useGetListData({
		type: TableTypes.UPDATE_HISTORY,
	});

	const baseDocuments = useMemo(
		() => getDocuments(props.pageType, props?.isForeign),
		[props.pageType, props?.isForeign]
	);

	const handleSave = (
		data: FormikValues,
		toggleEdit?: () => void,
		submit?: (newData?: FormikValues) => void,
		resetValue?: () => void
	) => {
		const record = list.find((item) => item.id === data?.id);
		const hasDocument = ALL_DOCUMENT_OPTIONS.find(
			(item) => item.value === data?.documentType
		)?.hasDocument;
		const newData = {
			id: record?.id,
			documentType: data?.documentType,
			type:
				record?.type === FileTypeEnum.UPLOAD
					? FileTypeEnum.UPLOAD
					: hasDocument && record?.type === FileTypeEnum.FORM
						? FileTypeEnum.FORM
						: FileTypeEnum.UPLOAD,
			currentRefTableId: props.pageType === PageTypeEnum.UNIT ? unitId : id,
			currentRefTableName: props.pageType,
		};
		submit?.(newData);
	};

	const columns = getColumns(
		baseDocuments,
		props.pageType,
		props.isForeign,
		t
	).map((col) => {
		if (!col.editable) {
			return col;
		}
		return {
			...col,
			onCell: (record: TDefineDocumentAreaDetailStore) => {
				return {
					record,
					editable: col.editable,
					dataIndex: col?.dataIndex,
					title: col.title,
					cellType: col.cellType,
					renderCell: col.renderCell,
					dropdownProps: col.dropdownProps,
					inputProps: col.inputProps,
					handleSave,
				};
			},
		};
	}) as ColumnTypes;

	useEffect(() => {
		dispatch(CommonActions.getAutocompleteSuggestionRequest());
	}, []);

	const handleDownloadDocumentsBeforeOpen = useCallback(async () => {
		if (rowSelection.selectedRowKeys.length === 0) {
			showAlert(t('message:ALERT_3'));
			return false;
		}
		return true;
	}, [rowSelection.selectedRowKeys, t]);

	const handleDownloadDocuments = useCallback(
		(reason: string, callback?: () => void) => {
			const paths: TDownloadDefineDocumentParams[] = [];

			rowSelection.selectedRows.forEach((item) => {
				paths.push({
					path: getFileNameFromPath(item?.path) || '',
					fileName: item.name,
					downloadReason: reason,
					downloadFileName: item.name,
				});
			});
			dispatch(
				DefineDocumentAreaActions.downloadDocumentsRequest({
					paths,
					pageType: props.pageType,
					zipFileName: props.zipFileName,
					callback,
				})
			);
		},
		[rowSelection.selectedRowKeys, props.pageType, props.zipFileName, t]
	);

	const showConfirmDelete = useCallback(() => {
		if (rowSelection?.selectedRowKeys.length === 0) {
			showAlert(t('message:no-file-to-delete'));
		} else {
			handleDeleteDocuments();
		}
	}, [rowSelection.selectedRowKeys, t]);

	const handleDeleteDocuments = useCallback(() => {
		showAlert(
			t('message:ALERT_17', { record: t('files') }),
			() => {
				dispatch(
					DefineDocumentAreaActions.deleteDocumentsRequest({
						params: {
							refTableName: props.pageType,
							refTableId: props.refTableId.toString(),
							history: false, //avatar, companySeal has value true
							id: rowSelection.selectedRowKeys,
						},
						callback: (error) => {
							if (error) {
								return;
							}
							showToast(t('message:DELETE_SUCCESS'));
							setPage((pre) => ({
								...pre,
								current: 1,
							}));
							dispatch(
								DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
									...selectParams,
									page: 1,
								})
							);
							dispatch(
								CommonActions.getUpdateHistoryListRequest({
									...updateHistoryParams,
									page: 1,
									refTableId: props.refTableId,
									refTableName: props.pageType as any,
									allData: false,
								})
							);
							rowSelection.onReset();
						},
					})
				);
			},
			NOOP
		);
	}, [
		rowSelection.selectedRowKeys,
		selectParams,
		t,
		updateHistoryParams,
		props.refTableId,
		props.pageType,
	]);

	const handlePageSizeChange = useCallback(
		(value: DefaultOptionType['value']) => {
			setPage({
				current: 1,
				pageSize: value as number,
			});

			dispatch(
				DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
					...selectParams,
					page: 1,
					pageSize: value as number,
				})
			);
		},
		[selectParams]
	);

	const handlePageChange = useCallback(
		(page: number) => {
			setPage((pre) => ({
				...pre,
				current: page,
			}));
			dispatch(
				DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
					...selectParams,
					page: page,
				})
			);
		},
		[selectParams]
	);

	const onClickUpload = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			if (isNull(e.target.files)) return;
			setLoading(true);
			const filesArray = Array.from(e.target.files);
			const hasInvalidFileType = filesArray.some((file) =>
				isFileTypeInvalid(FILE_ACCEPT, file.name)
			);
			const hasTooManyFiles = filesArray.length > MAX_FILE_UPLOAD;
			const hasOversizedInvalidImage = filesArray.some(
				(file) =>
					isFileTypeInvalid(IMAGE_TYPE, file.name) &&
					file.size > FILE_SIZE_10MB_BYTE
			);
			if (hasInvalidFileType) {
				showToast(t('message:COMMON_18'), {
					type: 'ERROR',
				});
				setLoading(false);
				return;
			}

			if (hasTooManyFiles) {
				showAlert(t('validation:ALERT_38', { maxFiles: MAX_FILE_UPLOAD }));
				setLoading(false);
				return;
			}

			if (hasOversizedInvalidImage) {
				showAlert(t('validation:ALERT_39', { maxSize: FILE_SIZE_10MB }));
				setLoading(false);
				return;
			}

			setFileList(filesArray);
			setIsUploadModalVisible(true);
			setLoading(false);
		},
		[t]
	);

	const reloadDefineDocumentAreaList = useCallback(() => {
		setPage((pre) => ({
			...pre,
			current: 1,
		}));
		if (filterRef.current?.isSearching) {
			filterRef.current?.onReset(true);
		} else {
			dispatch(
				DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
					...selectParams,
					page: 1,
				})
			);
			dispatch(
				CommonActions.getUpdateHistoryListRequest({
					...updateHistoryParams,
					page: 1,
					refTableId: props.refTableId,
					refTableName: props.pageType as any,
					allData: false,
				})
			);
		}
		rowSelection.onReset();
	}, [
		selectParams,
		filterRef.current,
		props.pageType,
		props.refTableId,
		updateHistoryParams,
	]);

	useEffect(() => {
		if (!open) {
			filterRef?.current?.onReset?.(true);
		}
	}, [open, filterRef]);

	const handleUploadClick = useCallback(() => {
		setIsUploadModalVisible(true);
	}, []);

	const handleCloseUploadModal = useCallback(() => {
		setIsUploadModalVisible(false);

		// reload list
		setPage((prev) => ({ ...prev, current: 1 }));
		filterRef?.current?.onReset?.(true);
		setLoading(true);
		dispatch(
			DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
				...selectParams,
				page: 1,
				callback: () => {
					setLoading(false);
				},
			})
		);
		dispatch(
			CommonActions.getUpdateHistoryListRequest({
				...updateHistoryParams,
				page: 1,
				refTableId: props.refTableId,
				refTableName: props.pageType as any,
				allData: false,
			})
		);
	}, [
		dispatch,
		filterRef,
		selectParams,
		updateHistoryParams,
		props.refTableId,
		props.pageType,
	]);

	return {
		columns,
		handleDownloadDocumentsBeforeOpen,
		handleDownloadDocuments,
		handleDeleteDocuments,
		handlePageSizeChange,
		totalCount,
		handlePageChange,
		rowSelection,
		showConfirmDelete,
		page,
		setPage,
		onClickUpload,
		reloadDefineDocumentAreaList,
		filterRef,
		loading,
		t,
		handleUploadClick,
		handleCloseUploadModal,
		isUploadModalVisible,
		fileList,
	};
};
